import { z } from 'zod';

import { zodEligibilityLabel, zodTimeStamp } from '@/utils/zodUtils';

export enum PlanVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  INVITE_ONLY = 'invite_only',
}

export enum PlanStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
}

export enum PlanBillingInterval {
  MONTH = 'month',
  YEAR = 'year',
}

export enum BillingAnchorMode {
  DAY_OF_MONTH = 'day_of_month',
  START_NOW = 'start_now',
}

export enum ScheduleHoursType {
  OPEN = 'open',
  CLOSED = 'closed',
}

export const DayOfWeek = z.enum([
  'mon',
  'tue',
  'wed',
  'thu',
  'fri',
  'sat',
  'sun',
]);

export const SchedulePeriodSchema = z.object({
  days_of_week: z.array(DayOfWeek).min(1),
  start_time_secs: z.number().int().min(0).max(86400),
  end_time_secs: z.number().int().min(0).max(86400),
  hours_type: z.nativeEnum(ScheduleHoursType),
});

export const ScheduleExceptionSchema = z.object({
  date: z.string(),
  hours_type: z.nativeEnum(ScheduleHoursType),
  reason: z.string().optional(),
});

export const PlanScheduleSchema = z.object({
  always_open: z.boolean().default(true),
  periods: z.array(SchedulePeriodSchema).default([]),
  exceptions: z.array(ScheduleExceptionSchema).default([]),
});

export const MAX_VEHICLE_CHANGES_PER_MONTH_MAX = 100;
export const VEHICLE_SWAP_COOLDOWN_HOURS_MAX = 720;
export const DEFAULT_MAX_VEHICLE_CHANGES_PER_MONTH = 3;
export const DEFAULT_VEHICLE_SWAP_COOLDOWN_HOURS = 0;
export const MAX_TRAILERS_MAX = 3;

export const PlanVersionSchema = z.object({
  id: z.string().uuid(),
  plan_id: z.string().uuid(),
  unit_amount_cents: z.number().int().min(0),
  currency: z.string().default('USD'),
  billing_interval: z.nativeEnum(PlanBillingInterval),
  billing_anchor_mode: z.nativeEnum(BillingAnchorMode),
  billing_anchor_day_of_month: z.number().int().min(1).max(28).nullable(),
  max_vehicles: z.number().int().min(1).nullable(),
  concurrent_vehicles: z.number().int().min(1).nullable(),
  trailer_required: z.boolean().default(false),
  max_trailers: z.number().int().min(0).max(MAX_TRAILERS_MAX).default(0),
  max_vehicle_changes_per_month: z
    .number()
    .int()
    .min(0)
    .max(MAX_VEHICLE_CHANGES_PER_MONTH_MAX)
    .default(DEFAULT_MAX_VEHICLE_CHANGES_PER_MONTH),
  vehicle_swap_cooldown_hours: z
    .number()
    .int()
    .min(0)
    .max(VEHICLE_SWAP_COOLDOWN_HOURS_MAX)
    .default(DEFAULT_VEHICLE_SWAP_COOLDOWN_HOURS),
  requires_reserved: z.boolean().default(false),
  is_not_renewable: z.boolean().default(false),
  effective_for_new_signups: z.boolean().default(false),
  schedule: PlanScheduleSchema.nullable().optional(),
  subscriber_count: z.number().int().min(0).default(0),
  created_at: z.string().datetime(),
});

export const PlanSchema = z.object({
  id: z.string().uuid(),
  location_id: z.string(),
  name: z.string().min(1),
  description: z.string().nullable(),
  visibility: z.nativeEnum(PlanVisibility),
  terms_html: z.string().nullable(),
  requires_eligibility_verification: z.boolean().default(false),
  eligibility_label: zodEligibilityLabel,
  stripe_product_id: z.string().nullable().optional(),
  is_archived: z.boolean().default(false),
  subscriber_count: z.number().int().min(0).default(0),
  versions: z.array(PlanVersionSchema).optional().default([]),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const PlanListSchema = z.array(PlanSchema);

const PlanVersionInputFieldsWithoutTrailerPolicy = z.object({
  unit_amount_cents: z.coerce
    .number()
    .min(0, 'Price must be a positive number'),
  currency: z.string().default('USD'),
  billing_interval: z
    .nativeEnum(PlanBillingInterval)
    .default(PlanBillingInterval.MONTH),
  billing_anchor_mode: z
    .nativeEnum(BillingAnchorMode)
    .default(BillingAnchorMode.START_NOW),
  billing_anchor_day_of_month: z.coerce
    .number()
    .int()
    .min(1)
    .max(28)
    .nullable()
    .optional(),
  max_vehicles: z.coerce.number().int().min(1).nullable().optional(),
  concurrent_vehicles: z.coerce.number().int().min(1).nullable().optional(),
  max_vehicle_changes_per_month: z.coerce
    .number()
    .int()
    .min(0)
    .max(MAX_VEHICLE_CHANGES_PER_MONTH_MAX)
    .default(DEFAULT_MAX_VEHICLE_CHANGES_PER_MONTH),
  vehicle_swap_cooldown_hours: z.coerce
    .number()
    .int()
    .min(0)
    .max(VEHICLE_SWAP_COOLDOWN_HOURS_MAX)
    .default(DEFAULT_VEHICLE_SWAP_COOLDOWN_HOURS),
  requires_reserved: z.boolean().default(false),
  is_not_renewable: z.boolean().default(false),
  schedule: PlanScheduleSchema.optional(),
});

const TrailerPolicyInputFields = z
  .object({
    trailer_required: z.boolean().default(false),
    max_trailers: z.coerce
      .number()
      .int()
      .min(0)
      .max(MAX_TRAILERS_MAX)
      .default(0),
  })
  .superRefine((data, ctx) => {
    if (data.trailer_required && data.max_trailers < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Max trailers must be at least 1 when trailers are required',
        path: ['max_trailers'],
      });
    }
  });

const PlanVersionInputFields = PlanVersionInputFieldsWithoutTrailerPolicy.and(
  TrailerPolicyInputFields
);

const planVersionAnchorRefinement = (
  data: z.infer<typeof PlanVersionInputFields>,
  ctx: z.RefinementCtx
) => {
  if (
    data.billing_anchor_mode === BillingAnchorMode.DAY_OF_MONTH &&
    data.billing_anchor_day_of_month == null
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Anchor day is required when using day of month billing',
      path: ['billing_anchor_day_of_month'],
    });
  }
};

export const CreatePlanVersionPayloadSchema =
  PlanVersionInputFields.superRefine(planVersionAnchorRefinement);

const PlanInputFieldsWithoutEligibility = z.object({
  name: z.string().min(1, 'Plan name is required'),
  description: z.string().nullable().optional(),
  visibility: z.nativeEnum(PlanVisibility).default(PlanVisibility.PRIVATE),
  terms_html: z.string().nullable().optional(),
  initial_version: CreatePlanVersionPayloadSchema.nullable().optional(),
});

const EligibilityInputFields = z.object({
  requires_eligibility_verification: z.boolean().default(false),
  eligibility_label: zodEligibilityLabel,
});

const planEligibilityRefinement = (
  data: {
    requires_eligibility_verification?: boolean;
    eligibility_label?: string | null;
  },
  ctx: z.RefinementCtx
) => {
  if (
    data.requires_eligibility_verification === false &&
    data.eligibility_label
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Eligibility label requires eligibility verification',
      path: ['eligibility_label'],
    });
  }
};

export const CreatePlanSchema = PlanInputFieldsWithoutEligibility.and(
  EligibilityInputFields.superRefine(planEligibilityRefinement)
);

export const UpdatePlanSchema = PlanInputFieldsWithoutEligibility.partial().and(
  EligibilityInputFields.partial().superRefine(planEligibilityRefinement)
);

export const CreatePlanVersionSchema = PlanVersionInputFields.superRefine(
  planVersionAnchorRefinement
);

export const ClonePlanVersionSchema = z.object({
  unit_amount_cents: z.coerce
    .number()
    .min(0, 'Price must be a positive number'),
});

export const RateChangeSchema = z.object({
  new_unit_amount_cents: z.coerce
    .number()
    .int()
    .min(1, 'Price must be at least $0.01'),
  send_notifications: z.boolean().default(true),
  notification_send_at: zodTimeStamp.optional(),
  group_subagreement_id: z.string().uuid().optional(),
});

export const RateChangeResponseSchema = z.object({
  version: PlanVersionSchema,
  subscriber_count: z.number().int().min(0),
  processing_queued: z.boolean(),
});

export type PlanVersionType = z.infer<typeof PlanVersionSchema>;
export type PlanType = z.infer<typeof PlanSchema>;
export type PlanListType = z.infer<typeof PlanListSchema>;
export type CreatePlanSchemaType = z.infer<typeof CreatePlanSchema>;
export type UpdatePlanSchemaType = z.infer<typeof UpdatePlanSchema>;
export type CreatePlanVersionSchemaType = z.infer<
  typeof CreatePlanVersionSchema
>;
export type CreatePlanVersionPayloadType = z.infer<
  typeof CreatePlanVersionPayloadSchema
>;
export type ClonePlanVersionSchemaType = z.infer<typeof ClonePlanVersionSchema>;
export type RateChangeSchemaType = z.infer<typeof RateChangeSchema>;
export type RateChangeResponseType = z.infer<typeof RateChangeResponseSchema>;
export type PlanScheduleType = z.infer<typeof PlanScheduleSchema>;
export type SchedulePeriodType = z.infer<typeof SchedulePeriodSchema>;
export type ScheduleExceptionType = z.infer<typeof ScheduleExceptionSchema>;
export type DayOfWeekType = z.infer<typeof DayOfWeek>;
